import { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusBadge } from "../components/Kpi";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Handshake } from "lucide-react";

export default function SiteVisits() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [greOpen, setGreOpen] = useState(false);
  const [gre, setGre] = useState({
    visit_id: "", client_full_name: "", mobile: "", alternate_mobile: "", email: "",
    occupation: "", company: "", residing_at: "", family_size: "",
    purchase_purpose: "end_use", decision_maker: "", budget_bracket: "",
    configuration_required: "", timeline: "3m", funding_source: "own",
    remarks: "", new_closing_manager_id: "",
  });
  const { user } = useAuth();
  const canHandover = ["super_admin","partner","management","gre","site_head","business_head"].includes(user?.role);

  const load = () => api.get("/visits").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/projects").then(r => setProjects(r.data));
    api.get("/users").then(r => setUsers(r.data));
  }, []);
  const closingMgrs = users.filter(u => u.role === "closing_manager" && u.active);

  const openHandover = (v) => { setGre({ ...gre, visit_id: v.id, client_full_name: v.client_name || "", new_closing_manager_id: "" }); setGreOpen(true); };
  const submitHandover = async () => {
    try { await api.post("/visits/gre-capture", gre); toast.success("Client data captured and handed over to Closing Manager"); setGreOpen(false); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6 animate-in">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Site</div>
        <h1 className="font-serif-display text-3xl">Site Visits</h1>
        <p className="text-sm text-muted-foreground mt-1">Includes physical visits and outstation video conference visits. Only Closing Managers assigned to a lead can see the client's contact number.</p>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Project</th><th className="text-left px-4 py-3">Mode</th><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Status</th>{canHandover && <th className="text-right px-4 py-3">Action</th>}</tr>
          </thead>
          <tbody>
            {items.map(v => {
              const p = projects.find(x => x.id === v.project_id);
              return (
                <tr key={v.id} className="border-t border-border hover:bg-accent/5">
                  <td className="px-4 py-3 font-mono text-xs">{v.visit_code}</td>
                  <td className="px-4 py-3">{v.client_name}</td>
                  <td className="px-4 py-3">{p?.name || "-"}</td>
                  <td className="px-4 py-3 text-xs capitalize">{v.visit_mode || "physical"}</td>
                  <td className="px-4 py-3 text-xs">{v.visit_date}</td>
                  <td className="px-4 py-3 capitalize">{v.visit_type || "-"}</td>
                  <td className="px-4 py-3"><StatusBadge status={v.status}/></td>
                  {canHandover && (
                    <td className="px-4 py-3 text-right">
                      {!v.handover_at ? (
                        <Button size="sm" variant="outline" onClick={() => openHandover(v)} data-testid={`gre-handover-${v.visit_code}`}><Handshake size={12} className="mr-1"/> GRE Capture & Hand Over</Button>
                      ) : (
                        <span className="text-[11px] text-emerald-700">Handed over</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={canHandover?"8":"7"} className="px-4 py-10 text-center text-muted-foreground">No site visits scheduled.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={greOpen} onOpenChange={setGreOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>GRE — Capture Client Data & Hand Over</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Applies to both physical and video-conference visits. After saving, the lead becomes visible to the Closing Manager only (contact number included).</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Client Full Name *</Label><Input value={gre.client_full_name} onChange={e => setGre({ ...gre, client_full_name: e.target.value })} data-testid="gre-name"/></div>
            <div><Label>Mobile *</Label><Input value={gre.mobile} onChange={e => setGre({ ...gre, mobile: e.target.value })} data-testid="gre-mobile"/></div>
            <div><Label>Alt Mobile</Label><Input value={gre.alternate_mobile} onChange={e => setGre({ ...gre, alternate_mobile: e.target.value })}/></div>
            <div><Label>Email</Label><Input value={gre.email} onChange={e => setGre({ ...gre, email: e.target.value })}/></div>
            <div><Label>Occupation</Label><Input value={gre.occupation} onChange={e => setGre({ ...gre, occupation: e.target.value })}/></div>
            <div><Label>Company</Label><Input value={gre.company} onChange={e => setGre({ ...gre, company: e.target.value })}/></div>
            <div><Label>Currently Residing At</Label><Input value={gre.residing_at} onChange={e => setGre({ ...gre, residing_at: e.target.value })}/></div>
            <div><Label>Family Size</Label><Input value={gre.family_size} onChange={e => setGre({ ...gre, family_size: e.target.value })}/></div>
            <div><Label>Purchase Purpose</Label>
              <Select value={gre.purchase_purpose} onValueChange={v => setGre({ ...gre, purchase_purpose: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["end_use","investment","upgrade"].map(x => <SelectItem key={x} value={x}>{x.replace(/_/g," ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Decision Maker</Label><Input value={gre.decision_maker} onChange={e => setGre({ ...gre, decision_maker: e.target.value })}/></div>
            <div><Label>Budget Bracket</Label><Input value={gre.budget_bracket} onChange={e => setGre({ ...gre, budget_bracket: e.target.value })} placeholder="e.g. 1-2 Cr"/></div>
            <div><Label>Configuration Required</Label><Input value={gre.configuration_required} onChange={e => setGre({ ...gre, configuration_required: e.target.value })} placeholder="e.g. 3 BHK"/></div>
            <div><Label>Timeline</Label>
              <Select value={gre.timeline} onValueChange={v => setGre({ ...gre, timeline: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["immediate","3m","6m","12m","exploratory"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Funding</Label>
              <Select value={gre.funding_source} onValueChange={v => setGre({ ...gre, funding_source: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["own","loan","both"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={gre.remarks} onChange={e => setGre({ ...gre, remarks: e.target.value })}/></div>
            <div className="col-span-2"><Label>Hand Over To Closing Manager *</Label>
              <Select value={gre.new_closing_manager_id} onValueChange={v => setGre({ ...gre, new_closing_manager_id: v })}>
                <SelectTrigger data-testid="gre-closing-select"><SelectValue placeholder="Select closing manager"/></SelectTrigger>
                <SelectContent>{closingMgrs.map(u => <SelectItem key={u.id} value={u.id}>{u.name} — {u.division}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={submitHandover} data-testid="gre-submit">Capture & Hand Over</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
